import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MasterCard from '../../../components/contentcards/base/MasterCard'
import handleToast from '../../../components/HandleToast'
import { completeContent, fetchContent } from '../../../api/content/content'
import { fetchPages } from '../../../api/content/contentPage'
import { addContentResponse } from '../../../api/content/contentResponse'
import { contentItems } from '../../../hooks/content/contentItems'
import { useRole } from '../../../hooks/auth/useRole'
import { ContentType } from '../../../types/content/content'
import { ContentItemType } from '../../../types/content/contentItems'
import { CONTENT_EXPERIENCE } from './contentExperienceStyles'

interface QuestionnaireProps {
  modalId?: string | null
  isModal?: boolean
  onComplete?: () => void
  onClose?: () => void
}

const isQuizPageName = (name: any) =>
  String(name ?? '').trim().toLowerCase() === 'quiz'

const normalizePageId = (page: any): string | undefined => {
  const rawPageId = typeof page === 'object' && page !== null ? page.id ?? page.pk ?? page.page : page
  if (rawPageId === undefined || rawPageId === null || rawPageId === '') return undefined
  return String(rawPageId)
}

const getPageIdentifiers = (page: any, fallbackIndex?: number) =>
  new Set(
    [page?.id, page?.pk, page?.page, page?.index, fallbackIndex]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .map((value) => String(value)),
  )

const questionBelongsToPage = (question: any, page: any, fallbackIndex?: number) => {
  const pageId = normalizePageId(question.page)
  if (!pageId) return false
  return getPageIdentifiers(page, fallbackIndex).has(pageId)
}

const optionText = (option: any) => (typeof option === 'string' ? option : option?.text ?? '')

const INDEXED_TYPES = new Set<ContentItemType>([
  ContentItemType.Short,
  ContentItemType.Paragraph,
  ContentItemType.SurveyMCQ,
  ContentItemType.SurveyRating,
  ContentItemType.TrainingMCQ,
  ContentItemType.TrainingCheckbox,
  ContentItemType.LearningCheckbox,
  ContentItemType.SurveyText,
])

export default function Questionnaire({
  modalId = null,
  isModal = false,
  onComplete,
  onClose,
}: QuestionnaireProps) {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { isUserMode } = useRole()
  const { useGetContentItemsListQuery } = contentItems()

  const contentId = modalId || id

  const contentQuery = useQuery({
    queryKey: ['content', contentId],
    queryFn: () => (contentId ? fetchContent(contentId) : Promise.reject(t('content.errors.noContentId'))),
    enabled: !!contentId,
  })

  const contentType: ContentType = (contentQuery.data?.content_type as ContentType) || ContentType.Skills
  const usesPagedFlow = contentType === ContentType.Training
  const isSurveyLike =
    contentType === ContentType.Survey ||
    contentType === ContentType.AnonymousSurvey ||
    contentType === ContentType.Learning

  const contentItemsQuery = contentId
    ? useGetContentItemsListQuery({ id: contentId })
    : { data: null, isLoading: false }

  const questions = useMemo(() => {
    const rawItems = Array.isArray(contentItemsQuery.data)
      ? contentItemsQuery.data
      : Array.isArray((contentItemsQuery.data as any)?.data)
      ? (contentItemsQuery.data as any).data
      : []

    return rawItems
      .map((question: any) => ({
        ...question,
        index: Number(question.index ?? 0),
        page: normalizePageId(question.page),
      }))
      .sort((a: any, b: any) => Number(a.index ?? 0) - Number(b.index ?? 0))
  }, [contentItemsQuery.data])

  const [pages, setPages] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [quizSubmitted, setQuizSubmitted] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [mcqSelections, setMcqSelections] = useState<Record<string, number | null>>({})
  const [checkboxSelections, setCheckboxSelections] = useState<Record<string, number[]>>({})
  const [surveyMCQSelections, setSurveyMCQSelections] = useState<Record<string, number | null>>({})
  const [surveyRatingSelections, setSurveyRatingSelections] = useState<Record<string, number | null>>({})
  const [surveyTextAnswer, setSurveyTextAnswer] = useState<Record<string, string | null>>({})

  useEffect(() => {
    const loadPages = async () => {
      if (!contentId || !usesPagedFlow) {
        setPages([])
        setCurrentPage(1)
        return
      }

      try {
        const res = await fetchPages(String(contentId))
        const pagesData: any[] = Array.isArray(res)
          ? res
          : Array.isArray((res as any)?.data)
          ? (res as any).data
          : []
        const sortedPages = [...pagesData].sort((a: any, b: any) => (a?.index || 0) - (b?.index || 0))
        const firstNonQuizIndex = sortedPages.findIndex((page: any) => !isQuizPageName(page?.name))

        setPages(sortedPages)
        setCurrentPage(firstNonQuizIndex >= 0 ? firstNonQuizIndex + 1 : 1)
      } catch (error) {
        console.error('Failed to fetch pages', error)
      }
    }

    loadPages()
  }, [contentId, usesPagedFlow])

  const currentPageData = usesPagedFlow ? pages[currentPage - 1] : null

  const currentPageQuestions = useMemo(() => {
    if (!usesPagedFlow) return questions
    if (!currentPageData) return questions
    return questions.filter((question: any) => questionBelongsToPage(question, currentPageData, currentPage))
  }, [currentPage, currentPageData, questions, usesPagedFlow])

  const logicalOrder = useMemo(() => {
    if (!usesPagedFlow) return [0]
    const indexedPages = pages.map((page: any, index: number) => ({ page, index }))
    const contentPages = indexedPages.filter(({ page }) => !isQuizPageName(page?.name)).map(({ index }) => index)
    const quizPages = indexedPages.filter(({ page }) => isQuizPageName(page?.name)).map(({ index }) => index)
    return [...contentPages, ...quizPages]
  }, [pages, usesPagedFlow])

  const logicalPosition = logicalOrder.indexOf(currentPage - 1)
  const isLastLogicalPage = logicalOrder.length === 0 || logicalPosition === logicalOrder.length - 1
  const currentPageObj = currentPageData
  const isCurrentQuizPage = !!currentPageObj && isQuizPageName(currentPageObj?.name)
  const showPageContext = usesPagedFlow && pages.length > 0

  const currentLearningUrlItem =
    contentType === ContentType.Learning
      ? currentPageQuestions.find((question: any) => question.type === ContentItemType.URL && typeof question.url === 'string' && question.url.trim() !== '')
      : null
  const currentLearningConfirmation =
    contentType === ContentType.Learning
      ? currentPageQuestions.find((question: any) => question.type === ContentItemType.LearningCheckbox)
      : null

  const visiblePageTitle = currentPageObj?.name || t('content.editor.pageLabel', { index: Math.max(1, logicalPosition + 1) })
  const visiblePageSubtitle = t('content.editor.pageSubtitle.default')

  const goToLogicalOffset = (offset: number) => {
    const nextPosition = logicalPosition + offset
    if (nextPosition < 0 || nextPosition >= logicalOrder.length) return
    setCurrentPage(logicalOrder[nextPosition] + 1)
  }

  const handleQuizSubmit = async () => {
    if (!contentId) return

    try {
      const trainingAnswers = questions
        .filter(
          (question: any) =>
            (question.type === ContentItemType.TrainingMCQ && question.id.toString() in mcqSelections) ||
            (question.type === ContentItemType.TrainingCheckbox && question.id.toString() in checkboxSelections),
        )
        .map((question: any) => {
          const questionId = question.id.toString()
          let answer: string | string[] = ''

          if (question.type === ContentItemType.TrainingMCQ) {
            const selectedIndex = mcqSelections[questionId]
            answer = selectedIndex !== undefined && selectedIndex !== null ? optionText(question.answers?.[selectedIndex]) : ''
          } else if (question.type === ContentItemType.TrainingCheckbox) {
            answer = (checkboxSelections[questionId] || [])
              .map((index) => optionText(question.answers?.[index]))
              .filter(Boolean)
          }

          return {
            type: question.type,
            question: questionId,
            answer,
          }
        })

      const surveyAnswers = questions
        .filter(
          (question: any) =>
            (question.type === ContentItemType.SurveyMCQ && question.id.toString() in surveyMCQSelections) ||
            (question.type === ContentItemType.SurveyRating && question.id.toString() in surveyRatingSelections) ||
            (question.type === ContentItemType.SurveyText && question.id.toString() in surveyTextAnswer) ||
            (question.type === ContentItemType.LearningCheckbox && surveyTextAnswer[question.id.toString()] === 'true'),
        )
        .map((question: any) => {
          const questionId = question.id.toString()
          let answer: any = null

          if (question.type === ContentItemType.SurveyMCQ) {
            const selectedIndex = surveyMCQSelections[questionId]
            answer = selectedIndex !== undefined && selectedIndex !== null ? optionText(question.answers?.[selectedIndex]) : null
          } else if (question.type === ContentItemType.SurveyRating) {
            answer = surveyRatingSelections[questionId] ?? null
          } else if (question.type === ContentItemType.SurveyText) {
            answer = surveyTextAnswer[questionId] ?? null
          } else if (question.type === ContentItemType.LearningCheckbox) {
            answer = true
          }

          return {
            type: question.type,
            question: questionId,
            answer,
          }
        })

      await addContentResponse(contentId, { answers: [...trainingAnswers, ...surveyAnswers] })
      setQuizSubmitted(true)
      handleToast(toast, true, t('content.toasts.responsesSubmittedTitle'), '')
      await queryClient.invalidateQueries({ queryKey: ['contentItemsList', String(contentId)] })
      onComplete?.()
    } catch (error) {
      console.error('Error submitting responses:', error)
      handleToast(
        toast,
        false,
        t('content.toasts.responsesSubmitFailedTitle'),
        error instanceof Error ? error.message : t('common.labels.unknownError'),
      )
    }
  }

  const handleCancel = () => {
    if (onClose) {
      onClose()
      return
    }

    setQuizSubmitted(false)
    setMcqSelections({})
    setCheckboxSelections({})
    setSurveyMCQSelections({})
    setSurveyRatingSelections({})
    setSurveyTextAnswer({})
  }

  const handleCompleteContent = async () => {
    if (!contentId) return

    try {
      setIsCompleting(true)
      await completeContent(contentId)
      setIsCompleted(true)
      handleToast(toast, true, t('content.toasts.completedTitle'), '')
      onComplete?.()
    } catch (error) {
      console.error('Error completing content:', error)
      handleToast(
        toast,
        false,
        t('content.toasts.completeFailedTitle'),
        error instanceof Error ? error.message : t('common.labels.unknownError'),
      )
    } finally {
      setIsCompleting(false)
    }
  }

  return (
    <Flex
      w="100%"
      h="100%"
      minH={isModal ? 'auto' : '100vh'}
      overflowY="auto"
      id="questionnaire-container"
      borderRadius="22px"
      bg={CONTENT_EXPERIENCE.pageBg}
      justify="center"
    >
      <Flex flex="1" direction="column" height="auto" w="100%" maxW="1440px">
        <Flex w="100%" direction="column" flex="1" bg="solid.0" minH={0}>
          <Flex
            flex="1"
            direction="column"
            overflowY="auto"
            px="0"
            py="0"
            gap="16px"
            minW={0}
            borderRadius={0}
            bg="transparent"
            border="none"
            boxShadow="none"
            m="0"
          >
            {showPageContext && (
              <Box
                borderRadius={CONTENT_EXPERIENCE.cardRadius}
                border="1px solid"
                borderColor={isCurrentQuizPage ? 'solid.900' : 'solid.50'}
                bg="transparent"
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
              >
                <Flex justify="space-between" align="flex-start" gap="16px" flexWrap="wrap">
                  <Box maxW="720px">
                    <Text
                      fontSize="12px"
                      fontWeight="700"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color={isCurrentQuizPage ? 'solid.900' : 'solid.500'}
                    >
                      {isCurrentQuizPage ? t('content.editor.stats.contentPage') : t('content.editor.stats.learningPage')}
                    </Text>
                    <Text mt="6px" fontSize="22px" fontWeight="700" color="solid.900">
                      {visiblePageTitle}
                    </Text>
                    <Text mt="6px" fontSize="14px" color="solid.600">
                      {visiblePageSubtitle}
                    </Text>
                  </Box>

                  <Flex justify="flex-end" gap="8px" flexWrap="wrap">
                    {!quizSubmitted ? (
                      <>
                        <Button variant="white" size="sm" onClick={handleCancel}>
                          {t('content.editor.actions.cancelAttempt')}
                        </Button>
                        {isCurrentQuizPage && (
                          <Button variant="mono" size="sm" onClick={handleQuizSubmit}>
                            {t('content.editor.actions.submitQuiz')}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button variant="white" size="sm" onClick={handleCancel}>
                        {isUserMode ? t('content.editor.actions.returnToContentOverview') : t('content.editor.actions.returnToAuthoring')}
                      </Button>
                    )}
                  </Flex>
                </Flex>

                <HStack spacing="10px" mt="16px" flexWrap="wrap">
                  {logicalOrder.map((pageIndex, index) => {
                    const page = pages[pageIndex]
                    const isActive = currentPage === pageIndex + 1
                    const label = page?.name || t('content.editor.pageLabel', { index: index + 1 })

                    return (
                      <Button
                        key={page?.id ?? `${label}-${index}`}
                        size="sm"
                        variant={isActive ? 'mono' : 'white'}
                        onClick={() => setCurrentPage(pageIndex + 1)}
                        borderRadius="8px"
                        minW="unset"
                        px="14px"
                      >
                        {label}
                      </Button>
                    )
                  })}
                </HStack>
              </Box>
            )}

            {contentType === ContentType.Learning && (
              <Box
                borderRadius={CONTENT_EXPERIENCE.cardRadius}
                border="1px solid"
                borderColor="solid.50"
                bg="transparent"
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
              >
                <Flex justify="space-between" align="flex-start" gap="16px" flexWrap="wrap">
                  <Box maxW="760px">
                    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="solid.500">
                      {t('content.editor.learningStep.label')}
                    </Text>
                    <Text mt="6px" fontSize="20px" fontWeight="700" color="solid.900">
                      {currentLearningUrlItem ? t('content.editor.learningStep.openTitle') : t('content.editor.learningStep.reviewTitle')}
                    </Text>
                    <Text mt="6px" fontSize="14px" color="solid.600">
                      {currentLearningUrlItem
                        ? t('content.editor.learningStep.openDescription')
                        : t('content.editor.learningStep.reviewDescription')}
                    </Text>
                  </Box>

                  <Flex gap="8px" flexWrap="wrap">
                    {currentLearningUrlItem?.url && (
                      <Button
                        as="a"
                        href={/^https?:\/\//i.test(currentLearningUrlItem.url) ? currentLearningUrlItem.url : `https://${currentLearningUrlItem.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="mono"
                        size="sm"
                      >
                        {t('content.editor.actions.launchLearning')}
                      </Button>
                    )}
                    {currentLearningConfirmation && (
                      <Button variant="white" size="sm" isDisabled>
                        {t('content.editor.learningStep.completionStepBelow')}
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Box>
            )}

            <VStack spacing="16px" align="stretch" pb={16}>
              {currentPageQuestions.length === 0 ? (
                <Flex align="center" justify="center" w="100%" py="48px">
                  <Text color="solid.200" textStyle="l-md">
                    {t('content.editor.emptyNoQuestions')}
                  </Text>
                </Flex>
              ) : (
                currentPageQuestions.map((question: any, index: number) => {
                  const displayIndex = currentPageQuestions
                    .slice(0, index)
                    .filter((candidate: any) => INDEXED_TYPES.has(candidate.type))
                    .length
                  const isSectionItem = question.type === ContentItemType.Section

                  return (
                    <Box
                      key={question.id}
                      borderRadius={isSectionItem ? '0' : '12px'}
                      overflow={isSectionItem ? 'visible' : 'hidden'}
                      bg={isSectionItem ? 'transparent' : 'background.White'}
                      boxShadow={isSectionItem ? 'none' : '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)'}
                    >
                      <MasterCard
                        quizMode={true}
                        quizSubmitted={quizSubmitted}
                        id={question.id}
                        index={INDEXED_TYPES.has(question.type) ? displayIndex : 0}
                        type={question.type}
                        text={
                          question.type === ContentItemType.LearningCheckbox
                            ? question.title || question.label || ''
                            : question.url || question.question || question.title || question.content || ''
                        }
                        label={question.label || question.short || ''}
                        checkboxText={question.checkbox_text || ''}
                        points={question.points || 0}
                        media={question.media || ''}
                        paragraph={question.paragraph || ''}
                        short={question.short || ''}
                        mcqAnswers={question.answers || []}
                        contentType={contentType || ContentType.Skills}
                        correct_feedback={question.correct_feedback}
                        incorrect_feedback={question.incorrect_feedback}
                        onMCQSelect={
                          question.type === ContentItemType.SurveyMCQ
                            ? (questionId, selectedIndex) => setSurveyMCQSelections((prev) => ({ ...prev, [questionId]: selectedIndex }))
                            : (questionId, selectedIndex) => setMcqSelections((prev) => ({ ...prev, [questionId]: selectedIndex }))
                        }
                        onCheckboxToggle={(questionId, optionIndex) => {
                          setCheckboxSelections((prev) => {
                            const current = prev[questionId] || []
                            const next = current.includes(optionIndex)
                              ? current.filter((index) => index !== optionIndex)
                              : [...current, optionIndex]
                            return { ...prev, [questionId]: next }
                          })
                        }}
                        onRatingSelect={(questionId, rating) => setSurveyRatingSelections((prev) => ({ ...prev, [questionId]: rating }))}
                        onAnswer={(questionId, answer) => setSurveyTextAnswer((prev) => ({ ...prev, [questionId]: answer }))}
                        mcqSelectedIndex={
                          question.type === ContentItemType.SurveyMCQ
                            ? surveyMCQSelections[String(question.id)] ?? null
                            : mcqSelections[String(question.id)] ?? null
                        }
                        checkboxSelectedIndexes={checkboxSelections[String(question.id)] ?? []}
                        ratingValue={surveyRatingSelections[String(question.id)] ?? null}
                        hasUser={true}
                      />
                    </Box>
                  )
                })
              )}
            </VStack>

            {!quizSubmitted && (contentType === ContentType.Training || isSurveyLike) && (
              <Flex
                position="sticky"
                bottom="0"
                w="100%"
                bg="white"
                zIndex={10}
                justify="flex-end"
                align="center"
                gap="8px"
                h="56px"
                minH="56px"
                py={0}
                mt="20px"
                flexWrap="nowrap"
                borderTopWidth="1px"
                borderColor="solid.50"
              >
                <Button variant="white" onClick={handleCancel}>
                  {t('content.editor.actions.cancelAttempt')}
                </Button>
                {usesPagedFlow && (
                  <Button
                    variant="mono"
                    onClick={() => goToLogicalOffset(-1)}
                    isDisabled={logicalOrder.length === 0 || logicalPosition <= 0}
                  >
                    {t('content.editor.actions.previousPage')}
                  </Button>
                )}
                {isLastLogicalPage ? (
                  <Button variant="mono" onClick={handleQuizSubmit}>
                    {contentType === ContentType.Training ? t('content.editor.actions.submitQuiz') : t('content.editor.actions.submitResponses')}
                  </Button>
                ) : (
                  <Button variant="mono" onClick={() => goToLogicalOffset(1)}>
                    {isCurrentQuizPage ? t('common.actions.finish') : t('content.editor.actions.nextPage')}
                  </Button>
                )}
              </Flex>
            )}

            {onComplete && !isCompleted && contentType !== ContentType.Training && !isSurveyLike && questions.length > 0 && (
              <Flex
                position="sticky"
                bottom="0"
                w="100%"
                bg="white"
                zIndex={10}
                justify="flex-end"
                align="center"
                gap="8px"
                h="56px"
                minH="56px"
                py={0}
                mt="20px"
                flexWrap="nowrap"
                borderTopWidth="1px"
                borderColor="solid.50"
              >
                <Button variant="mono" onClick={handleCompleteContent} isLoading={isCompleting}>
                  {t('common.actions.complete')}
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
